import {
  ClientAccessRole,
  ClientCompany,
  ClientCompanyPerson,
  ClientCompanyProduct,
  ClientCompanySummary,
} from '@data/utils/types';
import { useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';

import type { IError, ListResponse } from '@data';
import * as api from '../api';
import type { ClientCompanyUpdate } from '../api/clientCompanies';
import * as queryKeys from '../constants/keys';

export const useListClientCompanies = (organizationId?: string): UseQueryResult<ListResponse<ClientCompany>, IError> =>
  useQuery({
    queryKey: [queryKeys.CLIENT_COMPANIES, organizationId],
    queryFn: () => api.fetchClientCompanies(organizationId!),
    enabled: !!organizationId,
  });

export const useClientCompany = (organizationId?: string, clientId?: string): UseQueryResult<ClientCompany, IError> =>
  useQuery({
    queryKey: [queryKeys.CLIENT_COMPANY, organizationId, clientId],
    queryFn: () => api.fetchClientCompany(organizationId!, clientId!),
    enabled: !!organizationId && !!clientId,
  });

export const useUpdateClientCompany = (
  organizationId: string,
  clientId: string,
): UseMutationResult<void, IError, ClientCompanyUpdate & { clientId?: string }, unknown> => {
  const queryClient = useQueryClient();
  return useMutation({
    // `clientId` may be overridden per call: the Add-client flow writes to the client it has just
    // created, which no hook could have been given at render time.
    mutationFn: ({ clientId: target, ...values }) =>
      api.updateClientCompany(organizationId, target ?? clientId, values),
    // Both surfaces: this client's page, and the list that names them and counts their products.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.CLIENT_COMPANY, organizationId, clientId] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.CLIENT_COMPANIES, organizationId] });
    },
  });
};

export const useCreateClientCompany = (
  organizationId: string,
): UseMutationResult<ClientCompany, IError, { name: string; productIds?: string[] }, unknown> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: values => api.createClientCompany(organizationId, values),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.CLIENT_COMPANIES, organizationId] });
      // A product's client changed with it, and the products list carries that column.
      queryClient.invalidateQueries({ queryKey: [queryKeys.PM_PRODUCTS] });
    },
  });
};

export const useDeleteClientCompany = (organizationId: string): UseMutationResult<void, IError, string, unknown> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: clientId => api.deleteClientCompany(organizationId, clientId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.CLIENT_COMPANIES, organizationId] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.PM_PRODUCTS] });
    },
  });
};

export const useAddClientCompanyPerson = (
  organizationId: string,
  clientId: string,
): UseMutationResult<
  ClientCompanyPerson,
  IError,
  {
    firstName: string;
    lastName?: string;
    email: string;
    productIds?: string[];
    /** Left out means read: what somebody added without anybody saying otherwise may do. */
    role?: ClientAccessRole;
    /** The client's own top account. The API takes it only while the client has none. */
    isSuperAdmin?: boolean;
    clientId?: string;
  },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    // Same override as useUpdateClientCompany, for the same reason.
    mutationFn: ({ clientId: target, ...values }) =>
      api.addClientCompanyPerson(organizationId, target ?? clientId, values),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.CLIENT_COMPANY, organizationId, clientId] });
      // The flat client-accounts list feeds the product dialogs' recipient pickers.
      queryClient.invalidateQueries({ queryKey: [queryKeys.CLIENTS, organizationId] });
    },
  });
};

export const useUpdateClientCompanyPerson = (
  organizationId: string,
  clientId: string,
): UseMutationResult<
  ClientCompanyPerson,
  IError,
  { membershipId: string; role?: ClientAccessRole; productIds?: string[] },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ membershipId, ...values }) =>
      api.updateClientCompanyPerson(organizationId, clientId, membershipId, values),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.CLIENT_COMPANY, organizationId, clientId] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.CLIENTS, organizationId] });
    },
  });
};

export const useRemoveClientCompanyPerson = (
  organizationId: string,
  clientId: string,
): UseMutationResult<void, IError, string, unknown> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: membershipId => api.removeClientCompanyPerson(organizationId, clientId, membershipId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.CLIENT_COMPANY, organizationId, clientId] });
      queryClient.invalidateQueries({ queryKey: [queryKeys.CLIENTS, organizationId] });
    },
  });
};

/**
 * Nothing to invalidate: the link lives in a mail, not on this screen, so the person's row reads the
 * same before and after. The caller reports the send itself.
 */
export const useSendClientSetPasswordEmail = (
  organizationId: string,
  clientId: string,
): UseMutationResult<void, IError, string, unknown> =>
  useMutation({
    mutationFn: membershipId => api.sendClientSetPasswordEmail(organizationId, clientId, membershipId),
  });

export const useMyClientCompanies = (
  organizationId?: string,
  enabled = true,
): UseQueryResult<ListResponse<ClientCompanySummary>, IError> =>
  useQuery({
    queryKey: [queryKeys.MY_CLIENT_COMPANIES, organizationId],
    queryFn: () => api.fetchMyClientCompanies(organizationId!),
    enabled: enabled && !!organizationId,
  });

export const useClientColleagues = (
  organizationId?: string,
  clientCompanyId?: string,
  enabled = true,
): UseQueryResult<ListResponse<ClientCompanyPerson>, IError> =>
  useQuery({
    queryKey: [queryKeys.CLIENT_COLLEAGUES, organizationId, clientCompanyId],
    queryFn: () => api.fetchClientColleagues(organizationId!, clientCompanyId),
    enabled: enabled && !!organizationId,
  });

export const useUpdateClientAccess = (
  organizationId: string,
): UseMutationResult<
  ClientCompanyPerson,
  IError,
  {
    clientCompanyId: string;
    membershipId: string;
    role?: ClientAccessRole;
    productIds?: string[];
  },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ clientCompanyId, membershipId, ...values }) =>
      api.updateClientAccess(organizationId, clientCompanyId, membershipId, values),
    onSettled: () => queryClient.invalidateQueries({ queryKey: [queryKeys.CLIENT_COLLEAGUES, organizationId] }),
  });
};

export const useRemoveClientAccess = (
  organizationId: string,
): UseMutationResult<void, IError, { clientCompanyId: string; membershipId: string }, unknown> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ clientCompanyId, membershipId }) =>
      api.removeClientAccess(organizationId, clientCompanyId, membershipId),
    onSettled: () => queryClient.invalidateQueries({ queryKey: [queryKeys.CLIENT_COLLEAGUES, organizationId] }),
  });
};

export const useInviteClientColleague = (
  organizationId: string,
): UseMutationResult<
  ClientCompanyPerson,
  IError,
  {
    firstName: string;
    lastName?: string;
    email: string;
    productIds?: string[];
    role?: ClientAccessRole;
    clientCompanyId?: string;
  },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: values => api.inviteClientColleague(organizationId, values),
    onSettled: () => queryClient.invalidateQueries({ queryKey: [queryKeys.CLIENT_COLLEAGUES, organizationId] }),
  });
};

export const useClientProducts = (
  organizationId?: string,
): UseQueryResult<ListResponse<ClientCompanyProduct>, IError> =>
  useQuery({
    queryKey: [queryKeys.CLIENT_OWN_PRODUCTS, organizationId],
    queryFn: () => api.fetchClientProducts(organizationId!),
    enabled: !!organizationId,
  });
